"use client";

/**
 * Landing Page - Contact Section Component
 */

import { useState } from "react";
import { useTranslations } from "next-intl";
import { Linkedin, Mail, Phone } from "lucide-react";
import { cn } from "@/shared/core/utils";
import { Input } from "@/shared/presentation/components/ui/input";
import { Textarea } from "@/shared/presentation/components/ui/textarea";
import { Button } from "@/shared/presentation/components/ui/button";
import { Spinner } from "@/shared/presentation/components/custom";
import { useSubmitContactForm, landingValidation } from "../../domain/hooks";
import { getContactInfo } from "../../constants";

export function ContactSection() {
  const t = useTranslations("landing");
  const tCommon = useTranslations("common");
  const contactInfo = getContactInfo();

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    address: "",
    subject: "",
    message: "",
  });

  const [isFormValid, setIsFormValid] = useState(false);

  const {
    mutateAsync: submitForm,
    isPending: isLoading,
    isSuccess: isMessageSent,
  } = useSubmitContactForm();

  const updateFormData = (field: string, value: string) => {
    const newData = { ...formData, [field]: value };
    setFormData(newData);

    // Validate form
    const isValid =
      landingValidation.isEmailValid(newData.email) &&
      landingValidation.isNameValid(newData.name);
    setIsFormValid(isValid);
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    await submitForm(formData);

    // Reset form on success
    setFormData({
      name: "",
      email: "",
      phone: "",
      address: "",
      subject: "",
      message: "",
    });
    setIsFormValid(false);
  };

  return (
    <section id="contact" className="bg-primary/10 px-8 py-24 text-center">
      <div className="mx-auto flex max-w-5xl flex-col">
        {/* Section Header */}
        <div className="mb-10">
          <h2 className="mx-0 my-4 text-3xl uppercase tracking-[1px] text-primary-dark">
            {t("contact.title")}
          </h2>
          <hr className="mx-auto mb-4 h-[3px] max-w-[100px] rounded-full bg-primary p-0 outline-none" />
          <p className="text-base leading-7 text-foreground">
            {t("contact.subtitle")}
          </p>
        </div>

        {/* Contact Info & Form */}
        <div className="mt-4 flex flex-col gap-6 text-left capitalize lg:flex-row">
          {/* Contact Information */}
          <div className="flex flex-1 flex-col items-center justify-around gap-8 sm:flex-row sm:items-start lg:flex-col lg:items-start lg:justify-start">
            <div className="mb-4 flex flex-1 flex-col items-center sm:items-start lg:items-start">
              <span className="text-lg font-semibold text-primary-dark">
                {t("contact.officeAddress")}
              </span>
              <span className="text-sm text-foreground">
                {contactInfo.officeAddress}
              </span>
            </div>
            <div className="mb-4 flex flex-1 flex-col items-center sm:items-start lg:items-start">
              <span className="text-lg font-semibold text-primary-dark">
                {t("contact.workingHours")}
              </span>
              <span className="text-sm text-foreground">
                {contactInfo.workingHours}
              </span>
            </div>
          </div>

          {/* Contact Form */}
          <form
            className="flex flex-[2] flex-col gap-4"
            onSubmit={handleSubmit}
          >
            <div className="flex flex-col gap-4 md:flex-row">
              <Input
                className="flex-1 rounded-lg border border-solid border-primary-dark/50 p-4 outline-none focus:border-primary"
                type="text"
                placeholder={t("contact.form.name")}
                onChange={(e) => updateFormData("name", e.target.value)}
                value={formData.name}
              />
              <Input
                className="flex-1 rounded-lg border border-solid border-primary-dark/50 p-4 outline-none focus:border-primary"
                type="email"
                placeholder={t("contact.form.email")}
                onChange={(e) => updateFormData("email", e.target.value)}
                value={formData.email}
              />
            </div>
            <div className="flex flex-col gap-4 md:flex-row">
              <Input
                className="flex-1 rounded-lg border border-solid border-primary-dark/50 p-4 outline-none focus:border-primary"
                type="text"
                placeholder={t("contact.form.phone")}
                onChange={(e) => updateFormData("phone", e.target.value)}
                value={formData.phone}
              />
              <Input
                className="flex-1 rounded-lg border border-solid border-primary-dark/50 p-4 outline-none focus:border-primary"
                type="text"
                placeholder={t("contact.form.address")}
                onChange={(e) => updateFormData("address", e.target.value)}
                value={formData.address}
              />
            </div>
            <Input
              className="rounded-lg border border-solid border-primary-dark/50 p-4 outline-none focus:border-primary"
              type="text"
              placeholder={t("contact.form.subject")}
              onChange={(e) => updateFormData("subject", e.target.value)}
              value={formData.subject}
            />
            <Textarea
              className="rounded-lg border border-solid border-primary-dark/50 p-4 outline-none focus:border-primary"
              rows={5}
              placeholder={t("contact.form.message")}
              onChange={(e) => updateFormData("message", e.target.value)}
              value={formData.message}
            />

            {isMessageSent ? (
              <div className="my-0 ml-auto rounded-md py-2 font-medium capitalize text-green-700 transition-all">
                {t("contact.form.success")}
              </div>
            ) : (
              <Button
                type="submit"
                disabled={!isFormValid || isLoading}
                className={cn(
                  "my-0 ml-auto rounded-md bg-secondary px-4 py-2 font-medium capitalize text-white transition-all hover:bg-opacity-90",
                  {
                    "cursor-not-allowed bg-foreground": !isFormValid,
                    "cursor-pointer": isFormValid,
                  }
                )}
              >
                {isLoading ? (
                  <Spinner size="md" variant="white" />
                ) : (
                  t("contact.form.submit")
                )}
              </Button>
            )}
          </form>
        </div>

        {/* Social Links */}
        <div className="mt-16 flex flex-col items-center justify-between gap-6 sm:flex-row">
          {contactInfo.linkedIn && (
            <a
              href={contactInfo.linkedIn}
              target="_blank"
              rel="noopener noreferrer"
              className="flex text-primary-dark transition-all hover:text-primary"
            >
              <Linkedin className="h-8 w-8" />
              <span className="my-auto pl-2">{t("contact.linkedIn")}</span>
            </a>
          )}
          {contactInfo.phone && (
            <div className="flex text-primary-dark transition-all hover:text-primary">
              <Phone className="my-auto h-6 w-6" />
              <span className="my-auto pl-2">{contactInfo.phone}</span>
            </div>
          )}
          {contactInfo.email && (
            <div className="flex text-primary-dark transition-all hover:text-primary">
              <Mail className="my-auto h-6 w-6" />
              <span className="my-auto pl-2">{contactInfo.email}</span>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}

export default ContactSection;
